import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { Spinner } from '@/components/Spinner';
import { Page } from '@/components/layout/Page';
import {
  CAL_VARIATIONS,
  Raw,
  calHtml,
  distHtml,
  hashKey,
  histDist,
  introBox,
  intervalHtml,
  qtyHtml,
  type Distribution,
} from '@/features/data/shared/charts';
import { C, cv } from '@/features/data/shared/theme';
import { cls } from '@/utils/cls';

/* ── Types ────────────────────────────────────────────────────────────── */
type Behavior = { name: string; val: string; conf: number; color: string };
type LifecycleStage = { stage: string; p50: string; health: string; note: string };
type Pattern = { name: string; conf: number; color: string; desc: string };
type Economic = {
  revQ: number[];
  revLabels: string[];
  dso: number;
  paymentCompliance: string;
  revenueAtRisk: string;
  openOrderValue: string;
  ltv: string;
  ltvNote: string;
  fcfContrib: string;
  fcfNote: string;
};
type IntelRow = { lbl: string; val: string; sub: string; signal: string; sc: string };
type Encoding = {
  cls: string;
  clsColor: string;
  name: string;
  desc: string;
  conf: number;
  lastFit: string;
  events: string;
};
type Mem = { calendarData: number[][]; distributions: Distribution[]; encodings: Encoding[] };
type PastOrder = {
  id: string;
  date: string;
  qty: string;
  product: string;
  productId?: string;
  status: string;
  commit: string;
  risk: string;
  riskLabel: string;
};
type PredictedOrder = { date: string; conf: number; qty: string; product: string; signal: string };
type Customer = {
  id: string;
  name: string;
  city?: string;
  country?: string;
  loc: string;
  type: string;
  memory: number;
  memoryLabel: string;
  memoryColor: string;
  risk: string;
  riskLabel: string;
  lastOrder: string;
  lastOrderQty: string;
  avgOrderQty: string;
  ordersLTM: number;
  revYTD: string;
  revTrend: string;
  openOrders: number;
  nextDelivery: string;
  forecastability: string;
  interarrival: string;
  commitDrift: string;
  sig: string;
  economic: Economic;
  behaviors: Behavior[];
  lifecycle: LifecycleStage[];
  patterns: Pattern[];
  orders: { past: PastOrder[]; predicted: PredictedOrder[] };
  mem: Mem;
  intel: IntelRow[];
  behaviorCharts: { interarrival: number[]; quantity: number[] };
  profile?: ProfileData;
};
// Behavioral Profile + AI Readiness mock template (absorbed from the Objects page; cycled per customer).
type ProfileData = {
  encodings: { name: string; pct: number }[];
  dimensions: [string, string][];
  readiness: { icon: string; ok: boolean; dim: string; detail: string }[];
  pattern: string;
  patternConf: string;
  aiReady: boolean;
};
type EventRec = { type: string; date: string; cat: string; note: string; risk: boolean; color?: string };
type IntelCell = { l: string; v: string; s: string };
type LineItem = {
  id: string;
  product: string;
  productId?: string;
  qty: string;
  rdd: string;
  commit: string;
  risk: string;
  riskLabel: string;
  intel: IntelCell[];
  events: EventRec[];
};
type SoEvent = { product: string; qty: string; orderDate: string; commitDate: string; lineItems: LineItem[] };
type DowChart = { days: string[]; dots: Record<string, number[]> };
// Real aggregated ordering behavior from GET /api/data-app/customer/:id/behavior (event-behavior API).
type BehaviorData = {
  interarrival: number[];
  quantity: number[];
  dow: DowChart;
  interarrivalMode: string;
  interarrivalP50: number;
  interarrivalP90: number;
  quantityAvg: string;
  quantityStd: string;
  dowMode: string;
  commitDriftMedian: string;
  orders: number;
  historyMonths: number;
  scope: 'overall' | 'pair';
  pairsUsed: number;
};

/* ── Color helpers (page-specific; shared C/VAR/cv live in ./shared/charts) ── */
const riskColor = (r: string) => (r === 'ok' ? '#2A8055' : r === 'warn' ? '#D27A24' : '#BC2E3D');
const riskBg = (r: string) =>
  r === 'ok' ? 'rgba(42,128,85,.1)' : r === 'warn' ? 'rgba(210,122,36,.1)' : 'rgba(188,46,61,.1)';
const memColor = (m: number) => (m >= 80 ? '#2A8055' : m >= 60 ? '#D27A24' : '#BC2E3D');
const confColor = (c: number) => (c >= 80 ? '#2A8055' : c >= 60 ? '#D27A24' : '#BC2E3D');

/* ── Chart string builders ────────────────────────────────────────────── */
// Day-of-week is page-specific (Customers shows the dashed "advance window" lines); the rest of the SVG
// builders + Raw live in ./shared/charts.
function dowChartHtml(dow: DowChart, modeDay = '') {
  const days = dow.days;
  const dots = dow.dots;
  const w = 340,
    rh = 13,
    pad = 26,
    top = 4;
  // viewBox height fits all rows (was a fixed 58 that clipped Fri–Sun); height:auto + meet keep it centered/zoomable.
  const h = top + days.length * rh + 4;
  return (
    `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto">` +
    `<line x1="${pad}" y1="0" x2="${pad}" y2="${h}" stroke="#E4E0D8" stroke-width="0.5"/>` +
    [20, 34]
      .map((n) => {
        const x = (pad + n * ((w - pad - 6) / 46)).toFixed(1);
        return `<line x1="${x}" y1="${top}" x2="${x}" y2="${h - top}" stroke="#D27A24" stroke-width="1" stroke-dasharray="3,2"/>`;
      })
      .join('') +
    days
      .map((d, i) => {
        const yc = top + i * rh + rh / 2;
        return (
          `<text x="${pad - 2}" y="${yc + 3}" font-size="7.5" fill="#9295A5" text-anchor="end" font-family="Inter,sans-serif">${d}</text>` +
          (dots[d] || [])
            .map(
              (n) =>
                `<circle cx="${(pad + n * ((w - pad - 6) / 46)).toFixed(1)}" cy="${yc.toFixed(1)}" r="3" fill="${d === modeDay ? '#274483' : '#91A2C0'}"/>`,
            )
            .join('')
        );
      })
      .join('') +
    '</svg>'
  );
}

/* ── Small UI atoms ───────────────────────────────────────────────────── */
const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  overflow: 'hidden',
};
function ChartCard({
  title,
  sub,
  html,
  onExpand,
}: {
  title: string;
  sub: string;
  html: string;
  onExpand: (t: string, s: string, h: string) => void;
}) {
  return (
    <div style={cardStyle}>
      <div
        style={{
          padding: '10px 14px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{title}</div>
          <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>{sub}</div>
        </div>
        <button
          type="button"
          title="Expand"
          onClick={() => onExpand(title, sub, html)}
          className="chart-expand-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: 4,
            cursor: 'pointer',
            color: C.t3,
            border: '1px solid transparent',
            background: 'transparent',
            flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path
              d="M1 5V1H5M8 1H12V5M12 8V12H8M5 12H1V8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <Raw style={{ padding: '12px 14px' }} html={html} />
    </div>
  );
}

function enlargeSvgs(html: string) {
  return html.replace(/(<svg\b[^>]*?style="[^"]*?)height:[^;"]+/g, '$1height:320px');
}

function ChartModal({ title, sub, html, onClose }: { title: string; sub: string; html: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 24px 64px rgba(0,0,0,.3)',
          width: 'min(960px,92vw)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>{title}</div>
            <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>{sub}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 6,
              cursor: 'pointer',
              color: C.t3,
              border: `1px solid ${C.border}`,
              background: '#fff',
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <Raw
          style={{ padding: 24, overflow: 'auto' }}
          html={`<div style="width:100%;min-height:340px;display:flex;align-items:center;justify-content:center"><div style="width:100%">${enlargeSvgs(html)}</div></div>`}
        />
      </div>
    </div>
  );
}

/* ── Summary tab ──────────────────────────────────────────────────────── */
function SummaryTab({ c }: { c: Customer }) {
  const sc = memColor(c.memory);
  const fwd = c.intel.slice(0, 2);
  const derived = [
    { lbl: 'Time Between Orders', val: c.interarrival, sub: 'Median · Last 12mo', sc: c.memory >= 80 ? C.pos : C.warn },
    {
      lbl: 'Commit Reliability',
      val: c.commitDrift,
      sub: 'Avg drift from RDD',
      sc: parseFloat(c.commitDrift) <= 2 ? C.pos : parseFloat(c.commitDrift) <= 5 ? C.warn : C.neg,
    },
    { lbl: 'Forecastability', val: c.forecastability, sub: 'Model confidence', sc },
    { lbl: 'Memory Strength', val: c.memory + '%', sub: c.memoryLabel, sc },
  ];
  // Order-count facts ("Orders Last 12 Months") are intentionally omitted — order events are no longer loaded on
  // the Summary tab (they're deferred to the Orders / Ordering-Behavior tabs).
  const facts = [
    { l: 'Last Order', v: c.lastOrder, s: c.lastOrderQty },
    { l: 'Next Delivery Due', v: c.nextDelivery, s: 'Open order' },
    { l: 'Open Orders', v: c.openOrders + ' orders', s: 'Currently active' },
    { l: 'Revenue YTD', v: c.revYTD, s: c.revTrend + ' vs prior year' },
  ];
  const trends = [
    { l: 'Revenue Trend', v: c.revTrend, pos: c.revTrend.startsWith('+') },
    {
      l: 'Forecastability',
      v: c.forecastability,
      pos: c.forecastability === 'Very High' || c.forecastability === 'High',
    },
    { l: 'Avg Order Quantity', v: c.avgOrderQty, pos: true },
    { l: 'Memory Strength', v: c.memory + '% · ' + c.memoryLabel, pos: c.memory >= 80 },
  ];
  return (
    <div>
      {/* 6-cell intel bar */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6,1fr)',
          borderBottom: `1px solid ${C.border}`,
          background: '#fff',
        }}
      >
        {fwd.map((cell) => (
          <div
            key={cell.lbl}
            style={{ padding: '10px 14px', borderRight: `1px solid ${C.border}`, background: 'rgba(39,68,131,.02)' }}
          >
            <div
              style={{
                fontSize: 8,
                fontWeight: 700,
                color: C.blue,
                textTransform: 'uppercase',
                letterSpacing: '.08em',
                marginBottom: 3,
              }}
            >
              {cell.lbl}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, letterSpacing: '-0.02em', marginBottom: 2 }}>
              {cell.val}
            </div>
            <div style={{ fontSize: 9, color: C.t3 }}>{cell.sub}</div>
            <div style={{ fontSize: 9, fontWeight: 600, color: cv(cell.sc), marginTop: 2 }}>{cell.signal}</div>
          </div>
        ))}
        {derived.map((m, i) => (
          <div key={m.lbl} style={{ padding: '10px 14px', borderRight: i < 3 ? `1px solid ${C.border}` : undefined }}>
            <div
              style={{
                fontSize: 8,
                fontWeight: 700,
                color: C.t3,
                textTransform: 'uppercase',
                letterSpacing: '.08em',
                marginBottom: 3,
              }}
            >
              {m.lbl}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, letterSpacing: '-0.02em', marginBottom: 2 }}>
              {m.val}
            </div>
            <div style={{ fontSize: 9, color: m.sc, fontWeight: 600 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* Narrative */}
      <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
        <div
          style={{
            fontSize: 11,
            color: C.t2,
            lineHeight: 1.7,
            padding: '10px 12px',
            borderRadius: 6,
            borderLeft: `3px solid ${c.risk === 'ok' ? C.blue : c.risk === 'warn' ? C.warn : C.neg}`,
            background:
              c.risk === 'ok'
                ? 'rgba(39,68,131,.04)'
                : c.risk === 'warn'
                  ? 'rgba(210,122,36,.04)'
                  : 'rgba(188,46,61,.04)',
          }}
        >
          {c.sig}
        </div>
      </div>

      {/* Upcoming signals */}
      <div style={{ padding: '10px 20px', borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: C.t3,
            textTransform: 'uppercase',
            letterSpacing: '.1em',
            marginBottom: 7,
          }}
        >
          Upcoming · Historical Signals
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {c.intel.map((u) => (
            <div
              key={u.lbl}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '6px 0',
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <div style={{ fontSize: 10.5, fontWeight: 700, color: C.blue, minWidth: 120 }}>{u.lbl}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, minWidth: 80 }}>{u.val}</div>
              <div style={{ fontSize: 10, color: C.t3, flex: 1 }}>{u.sub}</div>
              <div style={{ fontSize: 9.5, fontWeight: 600, color: cv(u.sc) }}>{u.signal}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Facts + trends */}
      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Supply Chain Facts</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
              Source: SAP SD · VBAK/VBAP · Updated 47 min ago
            </div>
          </div>
          <div style={{ padding: '8px 14px' }}>
            {facts.map((m, i) => (
              <div
                key={m.l}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                }}
              >
                <span style={{ fontSize: 11, color: C.t2 }}>{m.l}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: C.t1 }}>{m.v}</div>
                  <div style={{ fontSize: 9.5, color: C.t3 }}>{m.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Business Trends</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
              What Keystone has derived from transaction history
            </div>
          </div>
          <div style={{ padding: '8px 14px' }}>
            {trends.map((m, i) => (
              <div
                key={m.l}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                }}
              >
                <span style={{ fontSize: 11, color: C.t2 }}>{m.l}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: m.pos ? C.pos : C.neg }}>{m.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PatternPackMatch profile={c.profile} />
    </div>
  );
}

/* ── Behavioral Profile (Pattern Pack Match · Encodings · Dimensions) — absorbed from Objects ──────────── */
const PROFILE_LABEL: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: '#9295A5',
  textTransform: 'uppercase',
  letterSpacing: '.1em',
};
// Pattern Pack Match — stays on the Summary tab.
function PatternPackMatch({ profile }: { profile?: ProfileData }) {
  if (!profile) return null;
  return (
    <div style={{ padding: '16px 20px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ ...PROFILE_LABEL, marginBottom: 8 }}>Pattern Pack Match</div>
      <div style={{ background: C.warm, borderRadius: 8, padding: '10px 12px', borderLeft: `3px solid ${C.blue}` }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.t1, marginBottom: 2 }}>{profile.pattern}</div>
        <div style={{ fontSize: 10.5, color: C.t3 }}>{profile.patternConf} match confidence</div>
      </div>
    </div>
  );
}

// Behavioral Encodings + Behavioral Dimensions (live overlay) — moved to the Ordering Behavior tab.
function BehavioralEncodingsDims({ profile, bh }: { profile?: ProfileData; bh?: BehaviorData }) {
  if (!profile) return null;
  const encColor = (p: number) => (p >= 80 ? C.blue : p >= 60 ? C.warn : C.neg);
  const encT = (p: number) => (p >= 80 ? C.t1 : p >= 60 ? C.warn : C.neg);
  const realDims: Record<string, string> = bh
    ? {
        'Interarrival P50': bh.interarrivalP50 ? `${bh.interarrivalP50} days` : '',
        'Interarrival P90': bh.interarrivalP90 ? `${bh.interarrivalP90} days` : '',
        'Most Common Order Day': bh.dowMode,
        'Avg Order Quantity': bh.quantityAvg === '—' ? '' : `${bh.quantityAvg} units`,
        'Quantity Std Dev': bh.quantityStd === '—' ? '' : `${bh.quantityStd} units`,
        'Commit Drift Median': bh.commitDriftMedian === '—' ? '' : bh.commitDriftMedian,
        'History Depth': bh.historyMonths ? `${bh.historyMonths} months` : '',
      }
    : {};
  const dims: [string, string][] = profile.dimensions.map(([k, v]) => [k, realDims[k] ? realDims[k] : v]);
  const anyLive = Object.values(realDims).some(Boolean);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div style={cardStyle}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Behavioral Encodings</div>
        </div>
        <div style={{ padding: '8px 14px' }}>
          {profile.encodings.map((e) => (
            <div
              key={e.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 0',
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <div style={{ fontSize: 11.5, color: C.t1, flex: 1 }}>{e.name}</div>
              <div style={{ width: 60, height: 4, borderRadius: 2, background: C.border, flexShrink: 0 }}>
                <div style={{ width: `${e.pct}%`, height: 4, borderRadius: 2, background: encColor(e.pct) }} />
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: encT(e.pct),
                  width: 30,
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                {e.pct}%
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={cardStyle}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Behavioral Dimensions</div>
          <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
            {anyLive ? 'Live values from event-behavior API' : 'Mined from order timing & quantity'}
          </div>
        </div>
        <div style={{ padding: '8px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
          {dims.map((d, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '4px 0',
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <span style={{ fontSize: 10.5, color: C.t3 }}>{d[0]}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{d[1]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Orders tab ───────────────────────────────────────────────────────── */
function OrdersTab({ c, soEvents }: { c: Customer; soEvents: Record<string, SoEvent> }) {
  const [tab, setTab] = useState<'open' | 'predicted' | 'history'>('open');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [activeSo, setActiveSo] = useState<string | null>(null);
  const [activeLi, setActiveLi] = useState<string | null>(null);
  const [liPage, setLiPage] = useState(1);
  const LI_PAGE_SIZE = 10;

  // Line items for the selected order come from a follow-up, order_id-filtered
  // item-lifecycle call (performant); the cached payload supplies the order list.
  const orderQuery = useQuery<SoEvent>({
    queryKey: ['data-app', 'order', activeSo, c.id],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/order/${encodeURIComponent(activeSo!)}?cust=${encodeURIComponent(c.id)}`);
      if (!r.ok) throw new Error('Failed to load order line items');
      return r.json();
    },
    enabled: !!activeSo,
    staleTime: 5 * 60 * 1000,
  });
  const liveOrder = activeSo && orderQuery.data ? orderQuery.data : null;
  // Prefer live line items for the selected order; fall back to the cached order.
  const orderFor = (id: string): SoEvent | undefined => (id === activeSo && liveOrder ? liveOrder : soEvents[id]);

  const open = c.orders.past.filter((o) => o.status === 'open');
  const closed = c.orders.past.filter((o) => o.status === 'closed');
  const preds = c.orders.predicted;
  const rows = tab === 'open' ? open : tab === 'history' ? closed : [];
  const q = search.toLowerCase();
  // Order-list pagination (10/page) — matches the Products page Orders tab.
  const [orderPage, setOrderPage] = useState(1);
  const ORD_PAGE_SIZE = 10;
  const filteredRows = rows.filter((o) => !q || o.id.toLowerCase().includes(q) || o.product.toLowerCase().includes(q));
  const ordPageCount = Math.max(1, Math.ceil(filteredRows.length / ORD_PAGE_SIZE));
  const ordSafe = Math.min(orderPage, ordPageCount);
  const pagedRows = filteredRows.slice((ordSafe - 1) * ORD_PAGE_SIZE, ordSafe * ORD_PAGE_SIZE);

  const selectSo = (id: string) => {
    setActiveSo(id);
    setActiveLi(null);
    setLiPage(1);
    // Only one order expanded at a time — collapse any previously-opened order's line items.
    setExpanded({ [id]: true });
  };

  const order = activeSo ? (orderFor(activeSo) ?? null) : null;
  // Auto-select the first line item of the active order (derived — no setState).
  const effectiveLi = activeLi ?? order?.lineItems[0]?.id ?? null;
  const li = order && effectiveLi ? order.lineItems.find((x) => x.id === effectiveLi) : null;

  // Inline line-item list for an expanded order. The selected order's items load
  // live (order_id-filtered call) and paginate 10/page; cached orders show as-is.
  const renderLineItems = (orderId: string) => {
    const isActive = orderId === activeSo;
    if (isActive && orderQuery.isLoading) {
      return <div style={{ padding: '10px 12px 10px 40px', fontSize: 10, color: C.t3 }}>Loading line items…</div>;
    }
    const so = orderFor(orderId);
    if (!so || !so.lineItems.length) {
      return <div style={{ padding: '10px 12px 10px 40px', fontSize: 10, color: C.t3 }}>No line items.</div>;
    }
    const items = so.lineItems;
    const paged = isActive;
    const pageCount = paged ? Math.max(1, Math.ceil(items.length / LI_PAGE_SIZE)) : 1;
    const safePage = Math.min(liPage, pageCount);
    const shown = paged ? items.slice((safePage - 1) * LI_PAGE_SIZE, safePage * LI_PAGE_SIZE) : items;
    return (
      <div>
        {shown.map((lineItem) => (
          <div
            key={lineItem.id}
            onClick={() => {
              // Selecting a line item must not reset line-item pagination — only switching orders does.
              if (orderId !== activeSo) selectSo(orderId);
              setActiveLi(lineItem.id);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px 8px 40px',
              borderBottom: `1px solid ${C.border}`,
              cursor: 'pointer',
              background: effectiveLi === lineItem.id ? 'rgba(39,68,131,.07)' : C.warm,
              borderLeft: `3px solid ${lineItem.risk === 'ok' ? 'rgba(42,128,85,.3)' : lineItem.risk === 'warn' ? 'rgba(210,122,36,.4)' : 'rgba(188,46,61,.5)'}`,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: C.blue, fontFamily: 'monospace' }}>
                  {lineItem.id}
                </span>
                <span
                  style={{
                    fontSize: 8.5,
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: 100,
                    background: riskBg(lineItem.risk),
                    color: riskColor(lineItem.risk),
                  }}
                >
                  {lineItem.riskLabel}
                </span>
              </div>
              <div style={{ fontSize: 10, color: C.t2 }}>{lineItem.product}</div>
              {lineItem.events[0]?.date ? (
                <div style={{ fontSize: 9.5, color: C.t3 }}>{lineItem.events[0].date}</div>
              ) : null}
            </div>
          </div>
        ))}
        {paged && pageCount > 1 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px 6px 40px',
              borderBottom: `1px solid ${C.border}`,
              background: C.warm,
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLiPage((p) => Math.max(1, p - 1));
              }}
              disabled={safePage <= 1}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: safePage <= 1 ? C.t3 : C.blue,
                background: 'none',
                border: 'none',
                cursor: safePage <= 1 ? 'default' : 'pointer',
                padding: 0,
              }}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: 9.5, color: C.t3 }}>
              {safePage} / {pageCount}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLiPage((p) => Math.min(pageCount, p + 1));
              }}
              disabled={safePage >= pageCount}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: safePage >= pageCount ? C.t3 : C.blue,
                background: 'none',
                border: 'none',
                cursor: safePage >= pageCount ? 'default' : 'pointer',
                padding: 0,
              }}
            >
              Next ›
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const pill = (key: 'open' | 'predicted' | 'history', label: string, count: number) => (
    <div
      onClick={() => {
        setTab(key);
        setSearch('');
        setOrderPage(1);
      }}
      style={{
        flex: 1,
        textAlign: 'center',
        padding: '4px 6px',
        borderRadius: 6,
        fontSize: 10,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        background: tab === key ? C.blue : C.warm,
        color: tab === key ? '#fff' : C.t3,
      }}
    >
      {label} <span style={{ opacity: 0.8 }}>{count}</span>
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      {/* left list */}
      <div
        style={{
          width: 240,
          flexShrink: 0,
          borderRight: `1px solid ${C.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: C.warm,
        }}
      >
        <div
          style={{ padding: '10px 10px 8px', borderBottom: `1px solid ${C.border}`, background: '#fff', flexShrink: 0 }}
        >
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {pill('open', 'Open', open.length)}
            {pill('predicted', 'Predicted', preds.length)}
            {pill('history', 'History', closed.length)}
          </div>
          <input
            type="text"
            placeholder="Search order #..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOrderPage(1);
            }}
            style={{
              width: '100%',
              padding: '5px 9px',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              fontSize: 11,
              color: C.t1,
              background: C.warm,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {tab !== 'predicted' && ordPageCount > 1 && (
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 8 }}
            >
              <button
                type="button"
                disabled={ordSafe <= 1}
                onClick={() => setOrderPage((p) => Math.max(1, p - 1))}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: ordSafe <= 1 ? C.t3 : C.blue,
                  cursor: ordSafe <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 10, color: C.t2 }}>
                {ordSafe} / {ordPageCount}
              </span>
              <button
                type="button"
                disabled={ordSafe >= ordPageCount}
                onClick={() => setOrderPage((p) => Math.min(ordPageCount, p + 1))}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: ordSafe >= ordPageCount ? C.t3 : C.blue,
                  cursor: ordSafe >= ordPageCount ? 'not-allowed' : 'pointer',
                }}
              >
                Next ›
              </button>
            </div>
          )}
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {tab === 'predicted'
            ? preds
                .filter((p) => !q || p.date.toLowerCase().includes(q) || p.product.toLowerCase().includes(q))
                .map((p, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '8px 12px',
                      borderBottom: `1px solid ${C.border}`,
                      background: '#fff',
                      opacity: 0.88,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 2,
                      }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.blue }}>{p.date}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, color: confColor(p.conf) }}>{p.conf}%</span>
                    </div>
                    <div style={{ fontSize: 10.5, fontWeight: 500, color: C.t2, marginBottom: 2 }}>
                      {p.qty} · {p.product}
                    </div>
                    <div style={{ fontSize: 9.5, color: C.t3, lineHeight: 1.4 }}>{p.signal}</div>
                  </div>
                ))
            : pagedRows.map((o) => {
                const hasDetail = !!soEvents[o.id];
                const isExp = !!expanded[o.id];
                return (
                  <div key={o.id}>
                    <div
                      className={cls(activeSo === o.id && 'cso-active')}
                      style={{
                        borderLeft: `3px solid ${o.risk === 'ok' ? 'rgba(42,128,85,.3)' : o.risk === 'warn' ? 'rgba(210,122,36,.4)' : 'rgba(188,46,61,.5)'}`,
                        background: activeSo === o.id ? 'rgba(39,68,131,.05)' : '#fff',
                        borderBottom: `1px solid ${C.border}`,
                        padding: '8px 12px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {hasDetail ? (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpanded((s) => (s[o.id] ? {} : { [o.id]: true }));
                            }}
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 3,
                              border: `1px solid ${C.border}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 700,
                              color: C.t3,
                              cursor: 'pointer',
                              flexShrink: 0,
                              lineHeight: 1,
                              userSelect: 'none',
                            }}
                          >
                            {isExp ? '−' : '+'}
                          </div>
                        ) : (
                          <div style={{ width: 16, flexShrink: 0 }} />
                        )}
                        <div onClick={() => selectSo(o.id)} style={{ flex: 1, cursor: 'pointer' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 3,
                            }}
                          >
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.blue, fontFamily: 'monospace' }}>
                              {o.id}
                            </span>
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                padding: '1px 7px',
                                borderRadius: 100,
                                background: riskBg(o.risk),
                                color: riskColor(o.risk),
                              }}
                            >
                              {o.riskLabel}
                            </span>
                          </div>
                          {/* An order isn't attributed to one product (the line items are), so the row shows
                                only the order id + earliest line-item event date. Once the order is opened, use
                                the live (complete) order's earliest event rather than the cached sample's. */}
                          <div style={{ fontSize: 10, color: C.t3 }}>
                            {o.id === activeSo && liveOrder ? liveOrder.orderDate : o.date}
                            {o.commit ? ' · Commit ' + o.commit : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                    {isExp && renderLineItems(o.id)}
                  </div>
                );
              })}
        </div>
      </div>

      {/* right detail */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!order ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              color: C.t3,
            }}
          >
            <div style={{ fontSize: 32, opacity: 0.15 }}>📋</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.t2 }}>Select an order</div>
            <div style={{ fontSize: 11 }}>Click any order to see line items and event timeline</div>
          </div>
        ) : (
          <>
            <div
              style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, background: '#fff', flexShrink: 0 }}
            >
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: C.t3,
                  textTransform: 'uppercase',
                  letterSpacing: '.08em',
                  marginBottom: 3,
                }}
              >
                Sales Order · SAP SD · VBAK/VBAP
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.t1,
                  fontFamily: 'monospace',
                  letterSpacing: '.02em',
                  marginBottom: 2,
                }}
              >
                {activeSo}
              </div>
              <div style={{ fontSize: 10.5, color: C.t3 }}>
                {order.product}
                {order.orderDate ? ' · Ordered ' + order.orderDate : ''}
                {order.commitDate ? ' · Commit ' + order.commitDate : ''}
                {' · '}
                {order.lineItems.length} line item{order.lineItems.length === 1 ? '' : 's'}
              </div>
            </div>
            {!li ? (
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 6,
                  color: C.t3,
                }}
              >
                <div style={{ fontSize: 22, opacity: 0.15 }}>⚡</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.t2 }}>Select a line item</div>
                <div style={{ fontSize: 11 }}>Click a line item to see its event timeline</div>
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div
                  style={{
                    padding: '10px 18px',
                    borderBottom: `1px solid ${C.border}`,
                    background: C.warm,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.t3,
                      textTransform: 'uppercase',
                      letterSpacing: '.08em',
                      marginBottom: 2,
                    }}
                  >
                    Line Item · SAP SD · VBAP
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, color: C.blue, fontFamily: 'monospace', marginBottom: 4 }}
                  >
                    {li.id}
                  </div>
                  {/* linked product (matches the linked customer name on the Products page) */}
                  <div
                    style={{
                      fontSize: 8,
                      fontWeight: 700,
                      color: C.t3,
                      textTransform: 'uppercase',
                      letterSpacing: '.08em',
                      marginBottom: 2,
                    }}
                  >
                    Product
                  </div>
                  {li.productId ? (
                    <Link
                      to={`/data/products?id=${encodeURIComponent(li.productId)}`}
                      style={{ fontSize: 12, fontWeight: 700, color: C.blue, textDecoration: 'none' }}
                    >
                      {li.product}
                    </Link>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{li.product}</span>
                  )}
                  {li.rdd || li.commit ? (
                    <div style={{ fontSize: 10, color: C.t3, marginTop: 4 }}>
                      {li.rdd ? 'RDD ' + li.rdd : ''}
                      {li.commit ? (
                        <>
                          {li.rdd ? ' · ' : ''}
                          Commit <span style={{ fontWeight: 700, color: riskColor(li.risk) }}>{li.commit}</span>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {/* intel bar */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4,1fr)',
                    borderBottom: `1px solid ${C.border}`,
                    background: '#fff',
                  }}
                >
                  {li.intel.map((cell, i) => (
                    <div
                      key={cell.l}
                      style={{ padding: '10px 14px', borderRight: i < 3 ? `1px solid ${C.border}` : undefined }}
                    >
                      <div
                        style={{
                          fontSize: 8,
                          fontWeight: 700,
                          color: C.t3,
                          textTransform: 'uppercase',
                          letterSpacing: '.08em',
                          marginBottom: 3,
                        }}
                      >
                        {cell.l}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, marginBottom: 2 }}>{cell.v}</div>
                      <div style={{ fontSize: 9.5, color: C.t3 }}>{cell.s}</div>
                    </div>
                  ))}
                </div>
                {/* timeline */}
                <div style={{ padding: '16px 18px' }}>
                  <div
                    style={{
                      fontSize: 8.5,
                      fontWeight: 700,
                      color: C.t3,
                      textTransform: 'uppercase',
                      letterSpacing: '.1em',
                      marginBottom: 14,
                    }}
                  >
                    Event Timeline · {li.id} · Source: SAP CDHDR/CDPOS
                  </div>
                  {li.events.map((e, i) => {
                    const isSignal = e.cat === 'Signal';
                    // Per-event-type color from the shared backend config (consistent across pages).
                    const dotColor = e.color || (e.risk ? C.neg : isSignal ? C.pos : C.blue);
                    const dotBg = e.color
                      ? `${e.color}22`
                      : e.risk
                        ? 'rgba(188,46,61,.08)'
                        : isSignal
                          ? 'rgba(42,128,85,.06)'
                          : 'rgba(39,68,131,.06)';
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 14,
                          padding: '12px 0',
                          borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexShrink: 0,
                            width: 20,
                          }}
                        >
                          <div
                            style={{
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              background: dotColor,
                              boxShadow: `0 0 0 3px ${dotBg}`,
                              marginTop: 3,
                              flexShrink: 0,
                            }}
                          />
                          {i < li.events.length - 1 && (
                            <div style={{ width: 1.5, flex: 1, background: C.border, marginTop: 5, minHeight: 14 }} />
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}
                          >
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: e.risk ? C.neg : C.t1 }}>
                              {e.type}
                            </span>
                            <span
                              style={{
                                fontSize: 8.5,
                                fontWeight: 700,
                                padding: '2px 7px',
                                borderRadius: 100,
                                background: isSignal ? 'rgba(42,128,85,.1)' : 'rgba(39,68,131,.1)',
                                color: isSignal ? C.pos : C.blue,
                              }}
                            >
                              {e.cat}
                            </span>
                          </div>
                          <div style={{ fontSize: 10, color: C.t3 }}>{e.date}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Ordering Behavior tab ────────────────────────────────────────────── */
function BehaviorTab({ c, dow }: { c: Customer; dow: DowChart }) {
  const [modal, setModal] = useState<{ title: string; sub: string; html: string } | null>(null);
  const expand = (title: string, sub: string, html: string) => setModal({ title, sub, html });

  // Behavior scope: "overall" (aggregated across the customer's products) or a specific customer×product pair.
  const [scope, setScope] = useState<'overall' | 'pair'>('overall');
  const [pairProduct, setPairProduct] = useState('');
  // Distinct products this customer is seen ordering (the meaningful pairs for the behavior API).
  const productOpts = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of c.orders.past) if (o.productId) m.set(o.productId, o.product || o.productId);
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [c.orders.past]);
  // Products Mix — distinct products this customer orders (paginated 10/page, like the Products page Customer Mix).
  const [mixPage, setMixPage] = useState(1);
  const MIX_PAGE_SIZE = 10;
  const mixPageCount = Math.max(1, Math.ceil(productOpts.length / MIX_PAGE_SIZE));
  const mixSafe = Math.min(mixPage, mixPageCount);
  const mixShown = productOpts.slice((mixSafe - 1) * MIX_PAGE_SIZE, mixSafe * MIX_PAGE_SIZE);
  const pairActive = scope === 'pair' && !!pairProduct;
  const { data: bh } = useQuery<BehaviorData>({
    queryKey: ['data-app', 'customer', c.id, 'behavior', pairActive, pairProduct],
    queryFn: async () => {
      const url = pairActive
        ? `/api/data-app/customer/${encodeURIComponent(c.id)}/behavior?product=${encodeURIComponent(pairProduct)}`
        : `/api/data-app/customer/${encodeURIComponent(c.id)}/behavior`;
      const r = await fetch(url);
      if (!r.ok) throw new Error('Failed to load behavior');
      return r.json();
    },
    enabled: !!c.id && (scope === 'overall' || !!pairProduct),
    staleTime: 5 * 60 * 1000,
  });
  const realIa = bh?.interarrival?.length ? bh.interarrival : null;
  const realQty = bh?.quantity?.length ? bh.quantity : null;
  // No event-behavior data at all (loaded, zero orders) — show a clear message instead of mock-fallback charts.
  const noBehavior = !!bh && (bh.orders ?? 0) === 0;
  const pairName = productOpts.find((o) => o.id === pairProduct)?.name ?? pairProduct;
  const scopeNote = pairActive
    ? `${pairName} × this customer · ${bh?.orders ?? 0} orders`
    : `Overall Ordering Behavior · ${bh?.orders ?? 0} orders`;

  const dists = c.mem.distributions;
  const templateInterDists = dists.filter((d) => /interarrival|interval/i.test(d.label));
  const templateQtyDists = dists.filter((d) => !/interarrival|interval/i.test(d.label));
  // Distributions = real histograms of the per-pair interarrival/quantity arrays (fall back to the template
  // distributions only when the real arrays are absent). These change with the selected scope/pair.
  const interDists = realIa ? [histDist('Interarrival (days)', realIa, 'd')] : templateInterDists;
  const qtyDists = realQty ? [histDist('Order quantity (units)', realQty, '')] : templateQtyDists;
  // Calendar has no per-pair real source → pick one of 4 deterministic mock variations by selection identity,
  // so it changes per pair but is stable for the same customer/pair.
  const variationKey = scope === 'pair' && pairProduct ? `${c.id}|${pairProduct}` : c.id;
  const calData = CAL_VARIATIONS[hashKey(variationKey) % CAL_VARIATIONS.length];

  const calLegend =
    '<div style="display:flex;align-items:center;gap:5px;margin-top:8px">' +
    '<span style="font-size:9px;color:#9295A5">Less</span>' +
    [0.05, 0.2, 0.45, 0.7, 0.9]
      .map((o) => `<div style="width:9px;height:9px;border-radius:2px;background:rgba(39,68,131,${o})"></div>`)
      .join('') +
    '<span style="font-size:9px;color:#9295A5">More</span></div>';
  const calBody = `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;overflow-x:auto">${calHtml(calData)}${calLegend}</div>`;

  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Behavior graphs (top of tab): scope toggle + charts — reflect Overall vs a customer×product pair. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 3, background: C.warm, borderRadius: 8, padding: 3 }}>
          {(['overall', 'pair'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: scope === s ? C.blue : 'transparent',
                color: scope === s ? '#fff' : C.t2,
              }}
            >
              {s === 'overall' ? 'Overall Ordering Behavior' : 'By Product'}
            </button>
          ))}
        </div>
        {scope === 'pair' && (
          <select
            value={pairProduct}
            onChange={(e) => setPairProduct(e.target.value)}
            style={{
              fontSize: 11,
              padding: '5px 9px',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.t1,
              background: '#fff',
              maxWidth: 320,
            }}
          >
            <option value="">Select a product…</option>
            {productOpts.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: C.t2,
          background: 'rgba(39,68,131,.05)',
          borderLeft: `3px solid ${C.blue}`,
          borderRadius: 6,
          padding: '6px 10px',
        }}
      >
        {scope === 'pair' && !pairProduct
          ? 'Select a product to see this customer×product ordering behavior.'
          : scopeNote}
      </div>
      {noBehavior && (
        <div
          style={{
            padding: '28px 20px',
            textAlign: 'center',
            background: C.warm,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
          }}
        >
          <div style={{ fontSize: 22, opacity: 0.25, marginBottom: 6 }}>📭</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.t2, marginBottom: 4 }}>No event behavior data</div>
          <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.5 }}>
            {pairActive
              ? `No recorded order events for ${pairName} × this customer.`
              : 'No recorded order events for this customer in the behavior feed.'}
          </div>
        </div>
      )}
      {!noBehavior && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
          <ChartCard
            title="Ordering Calendar — Last 12 Months"
            sub="Source: VBAK.ERDAT · Darker = more orders · Feeds calendar rhythm encoding"
            html={calBody}
            onExpand={expand}
          />
          <ChartCard
            title="Day-of-Week Ordering Pattern"
            sub={
              bh?.dow ? `Most frequent: ${bh.dowMode || '—'}` : 'Source: VBAK.ERDAT · Feeds calendar rhythm encoding'
            }
            html={dowChartHtml(bh?.dow ?? dow, bh?.dowMode ?? '')}
            onExpand={expand}
          />
          <ChartCard
            title="Time Between Orders — Last 45 Orders"
            sub={
              realIa
                ? `Mode ${bh?.interarrivalMode || '—'}d · P50 ${bh?.interarrivalP50}d · P90 ${bh?.interarrivalP90}d`
                : 'Source: VBAK.ERDAT · Used as interarrival prior in BFG model'
            }
            html={intervalHtml(realIa ?? c.behaviorCharts.interarrival)}
            onExpand={expand}
          />
          {interDists.length > 0 && (
            <ChartCard
              title="Interarrival Distribution"
              sub="Shape shows predictability — tighter = more forecastable"
              html={distHtml(interDists)}
              onExpand={expand}
            />
          )}
          <ChartCard
            title="Order Quantity — Last 45 Orders"
            sub={
              realQty
                ? `Avg ${bh?.quantityAvg} · ${bh?.quantityStd}`
                : 'Source: VBAP.KWMENG · Orange line = rolling average'
            }
            html={qtyHtml(realQty ?? c.behaviorCharts.quantity)}
            onExpand={expand}
          />
          {qtyDists.length > 0 && (
            <ChartCard
              title="Order Quantity Distribution"
              sub="Histogram of order sizes — feeds quantity norm encoding"
              html={distHtml(qtyDists)}
              onExpand={expand}
            />
          )}
        </div>
      )}
      {/* Ordering Signals + Products Mix (mirrors the Products page Demand Signals + Customer Mix) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Ordering Signals</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
              Patterns extracted from order timing, quantity, and cadence
            </div>
          </div>
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {c.behaviors.map((b) => (
              <div key={b.name}>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{b.name}</div>
                    <div style={{ fontSize: 10, color: C.t3 }}>{b.val}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: cv(b.color) }}>{b.conf}%</div>
                    <div style={{ fontSize: 9, color: C.t3 }}>confidence</div>
                  </div>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: C.border }}>
                  <div style={{ height: 5, borderRadius: 3, background: cv(b.color), width: `${b.conf}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Products Mix</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>What this customer orders</div>
          </div>
          <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mixPageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <button
                  type="button"
                  disabled={mixSafe <= 1}
                  onClick={() => setMixPage((m) => Math.max(1, m - 1))}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: `1px solid ${C.border}`,
                    background: '#fff',
                    color: mixSafe <= 1 ? C.t3 : C.blue,
                    cursor: mixSafe <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ‹ Prev
                </button>
                <span style={{ fontSize: 10, color: C.t2 }}>
                  {mixSafe} / {mixPageCount}
                </span>
                <button
                  type="button"
                  disabled={mixSafe >= mixPageCount}
                  onClick={() => setMixPage((m) => Math.min(mixPageCount, m + 1))}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: `1px solid ${C.border}`,
                    background: '#fff',
                    color: mixSafe >= mixPageCount ? C.t3 : C.blue,
                    cursor: mixSafe >= mixPageCount ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next ›
                </button>
              </div>
            )}
            {mixShown.length ? (
              mixShown.map((pr) => (
                <div
                  key={pr.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    background: C.warm,
                    borderRadius: 6,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link
                      to={`/data/products?id=${encodeURIComponent(pr.id)}`}
                      style={{ fontSize: 11.5, fontWeight: 700, color: C.blue, textDecoration: 'none' }}
                    >
                      {pr.name}
                    </Link>
                    <div style={{ fontSize: 9, color: C.t3, fontFamily: 'monospace' }}>{pr.id}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ fontSize: 10.5, color: C.t3 }}>No products in the current sample.</div>
            )}
          </div>
        </div>
      </div>
      {/* Behavioral Encodings + Behavioral Dimensions (moved here from the Summary tab) */}
      <BehavioralEncodingsDims profile={c.profile} bh={bh} />

      {modal && <ChartModal title={modal.title} sub={modal.sub} html={modal.html} onClose={() => setModal(null)} />}

      {c.mem.encodings.length > 0 ? (
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Encoding Strength by Norm</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>Direct inputs to the BFG Foundation Model</div>
          </div>
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {c.mem.encodings.map((enc) => (
              <div key={enc.name}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <div style={{ flex: 1, paddingRight: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                      <span
                        style={{
                          fontSize: 8,
                          fontWeight: 700,
                          padding: '1px 5px',
                          borderRadius: 100,
                          background:
                            enc.clsColor === 'var(--blue-dark)'
                              ? 'rgba(39,68,131,.1)'
                              : enc.clsColor === 'var(--positive)'
                                ? 'rgba(42,128,85,.1)'
                                : 'rgba(139,92,246,.1)',
                          color: cv(enc.clsColor),
                        }}
                      >
                        {enc.cls}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{enc.name}</span>
                    </div>
                    <div style={{ fontSize: 9.5, color: C.t3, lineHeight: 1.4 }}>{enc.desc}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: confColor(enc.conf) }}>{enc.conf}%</div>
                    <div style={{ fontSize: 9, color: C.t3 }}>{enc.lastFit}</div>
                  </div>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: C.border }}>
                  <div style={{ height: 4, borderRadius: 2, background: confColor(enc.conf), width: `${enc.conf}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

/* ── Lifecycle tab ────────────────────────────────────────────────────── */
function LifecycleTab({ c }: { c: Customer }) {
  const hc = (s: string) => (s === 'ok' ? C.pos : s === 'warn' ? C.warn : C.neg);
  const hb = (s: string) =>
    s === 'ok' ? 'rgba(42,128,85,.05)' : s === 'warn' ? 'rgba(210,122,36,.05)' : 'rgba(188,46,61,.05)';
  const portAvg = [1, 21, 1, 1, 30];
  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {introBox(
        'Every stage transition below is measured from actual SAP timestamps — order creation date, commit date changes, goods issue, billing document, and payment clearing. These Δt measurements feed the Lifecycle model and Commit Advisor directly. If a stage shows friction here, that same friction appears in your cash conversion cycle.',
        'Reconstructed from SAP SD · CDHDR/CDPOS change documents',
      )}
      <div style={cardStyle}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Order-to-Cash Stage Timings · {c.name}</div>
          <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
            Median Δt at each stage vs portfolio average · Last 12 months
          </div>
        </div>
        <div style={{ padding: 16 }}>
          {c.lifecycle.map((s, i) => {
            const pa = portAvg[i] || 1;
            const cvNum = parseFloat(s.p50);
            const better = cvNum <= pa;
            return (
              <div
                key={s.stage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                }}
              >
                <div style={{ width: 160, fontSize: 11, fontWeight: 600, color: C.t1, flexShrink: 0 }}>{s.stage}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: hc(s.health) }}>{s.p50}</div>
                    <div style={{ fontSize: 9.5, color: C.t3 }}>vs portfolio avg {pa}d</div>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: better ? C.pos : C.neg }}>
                      {better ? '▲ Better' : '▼ Slower'}
                    </div>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: C.border }}>
                    <div
                      style={{
                        height: 5,
                        borderRadius: 3,
                        background: hc(s.health),
                        width: `${Math.min((cvNum / 45) * 100, 100)}%`,
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    width: 140,
                    flexShrink: 0,
                    padding: '4px 8px',
                    background: hb(s.health),
                    borderRadius: 6,
                    borderLeft: `2px solid ${hc(s.health)}`,
                  }}
                >
                  <div style={{ fontSize: 9.5, color: C.t2 }}>{s.note}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ ...cardStyle, padding: '14px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, marginBottom: 10 }}>Cash Conversion Implications</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <div style={{ padding: '10px 12px', background: C.warm, borderRadius: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>{c.economic.dso}d</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 2 }}>Days Sales Outstanding</div>
          </div>
          <div style={{ padding: '10px 12px', background: C.warm, borderRadius: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: c.economic.revenueAtRisk === '$0' ? C.pos : C.neg }}>
              {c.economic.revenueAtRisk}
            </div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 2 }}>Revenue at Risk</div>
          </div>
          <div style={{ padding: '10px 12px', background: C.warm, borderRadius: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>{c.economic.paymentCompliance}</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 2 }}>Payment Compliance</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Patterns tab ─────────────────────────────────────────────────────── */
function PatternsTab({ c }: { c: Customer }) {
  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {introBox(
        "Patterns are matched by comparing this customer's behavioral signatures against Keystone's pattern library. Each match has a confidence score. Matched patterns are encoded as priors in the BFG Foundation Model — they are the mechanism by which we move beyond time-series forecasting to customer behavior prediction.",
        'Matched from behavioral history · Feed directly into forecast models',
      )}
      {c.patterns.map((p) => (
        <div key={p.name} style={cardStyle}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{p.name}</div>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: 100,
                background:
                  p.conf >= 80 ? 'rgba(42,128,85,.1)' : p.conf >= 60 ? 'rgba(210,122,36,.1)' : 'rgba(188,46,61,.1)',
                color: cv(p.color),
              }}
            >
              {p.conf}% confidence
            </span>
          </div>
          <div style={{ padding: '10px 14px' }}>
            <div style={{ height: 5, borderRadius: 3, background: C.border, marginBottom: 10 }}>
              <div style={{ height: 5, borderRadius: 3, background: cv(p.color), width: `${p.conf}%` }} />
            </div>
            <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6, marginBottom: 8 }}>{p.desc}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  padding: '4px 10px',
                  background: 'rgba(39,68,131,.06)',
                  borderRadius: 6,
                  fontSize: 9.5,
                  color: C.blue,
                  fontWeight: 600,
                }}
              >
                → Used in BFG Foundation Model
              </div>
              <div
                style={{
                  padding: '4px 10px',
                  background: 'rgba(39,68,131,.06)',
                  borderRadius: 6,
                  fontSize: 9.5,
                  color: C.blue,
                  fontWeight: 600,
                }}
              >
                → Feeds Commit Advisor
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Economic Value tab ───────────────────────────────────────────────── */
function EconomicTab({ c }: { c: Customer }) {
  const t = c.economic.revQ;
  const mx = Math.max(...t);
  const perDay = Math.round((parseFloat(c.revYTD.replace('$', '').replace('M', '')) * 1000) / 365);
  const fin = [
    { l: 'Lifetime Value (24mo)', v: c.economic.ltv, s: c.economic.ltvNote, risk: false },
    { l: 'Open Order Value', v: c.economic.openOrderValue, s: 'Currently active', risk: false },
    {
      l: 'Revenue at Risk',
      v: c.economic.revenueAtRisk,
      s: 'Commit risk exposure',
      risk: c.economic.revenueAtRisk !== '$0',
    },
    { l: 'Days Sales Outstanding', v: c.economic.dso + 'd', s: 'Rolling average', risk: false },
    { l: 'Payment Compliance', v: c.economic.paymentCompliance, s: '% paid on terms', risk: false },
    {
      l: 'FCF Contribution',
      v: c.economic.fcfContrib,
      s: c.economic.fcfNote,
      risk: c.economic.fcfContrib.startsWith('-'),
    },
  ];
  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {introBox(
        "The economic view quantifies what's at stake if forecasting for this customer is wrong. Revenue at risk, DSO trends, and payment compliance are inputs to the Decisions Engine — they determine how aggressively Keystone recommends action when a signal fires for this customer.",
        'Why this matters to the model',
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Revenue by Quarter</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
              {c.revTrend} trend · {c.revYTD} YTD
            </div>
          </div>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80, marginBottom: 6 }}>
              {t.map((v, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 3,
                    height: '100%',
                  }}
                >
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.t2 }}>${v}M</div>
                  <div
                    style={{
                      width: '100%',
                      borderRadius: '3px 3px 0 0',
                      background: i === t.length - 1 ? C.blue : 'rgba(39,68,131,.3)',
                      height: Math.round((v / mx) * 60),
                    }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {c.economic.revLabels.map((l) => (
                <div key={l} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: C.t3 }}>
                  {l}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Financial Metrics</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>Cash, margin, and risk position</div>
          </div>
          <div style={{ padding: '8px 14px' }}>
            {fin.map((m, i) => (
              <div
                key={m.l}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 0',
                  borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                }}
              >
                <span style={{ fontSize: 11, color: C.t2 }}>{m.l}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: m.risk ? C.neg : C.t1 }}>{m.v}</div>
                  <div style={{ fontSize: 9.5, color: C.t3 }}>{m.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ ...cardStyle, padding: '14px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, marginBottom: 8 }}>
          What a 1-day forecast error costs
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <div
            style={{
              padding: '10px 12px',
              background: 'rgba(188,46,61,.04)',
              borderRadius: 6,
              borderLeft: `2px solid ${C.neg}`,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: C.neg }}>~${perDay}K</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 2 }}>Revenue exposure per day of slip</div>
          </div>
          <div
            style={{
              padding: '10px 12px',
              background: 'rgba(210,122,36,.04)',
              borderRadius: 6,
              borderLeft: `2px solid ${C.warn}`,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: C.warn }}>{c.economic.dso}d</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 2 }}>Cash locked up on average</div>
          </div>
          <div
            style={{
              padding: '10px 12px',
              background: 'rgba(39,68,131,.04)',
              borderRadius: 6,
              borderLeft: `2px solid ${C.blue}`,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: C.blue }}>{c.economic.paymentCompliance}</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 2 }}>Historical payment reliability</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tab bar ──────────────────────────────────────────────────────────── */
const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'orders', label: 'Orders' },
  { key: 'behavior', label: 'Ordering Behavior' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'patterns', label: 'Patterns' },
  { key: 'economic', label: 'Economic Value' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/* ── Page ─────────────────────────────────────────────────────────────── */
export function DataCustomers() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Only honor a customer id (guards against a stale ?id=prod_… leaking in from a product link/nav).
  const rawUrlId = searchParams.get('id');
  const urlId = rawUrlId && rawUrlId.startsWith('cust_') ? rawUrlId : null;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('summary');
  const [search, setSearch] = useState('');
  // Initial list page from ?page= (1-based) so a paginated view is a shareable permalink. Coexists with ?id=.
  const urlPage = (() => {
    const n = parseInt(searchParams.get('page') ?? '', 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  })();
  const [page, setPage] = useState(urlPage);
  const PAGE_SIZE = 10;
  // Change the list page and sync ?page= (dropped at page 1 to keep URLs clean).
  const goToPage = (n: number) => {
    setPage(n);
    setSearchParams(
      (prev) => {
        if (n <= 1) prev.delete('page');
        else prev.set('page', String(n));
        return prev;
      },
      { replace: true },
    );
  };
  // Selecting a customer writes ?id=<custId> so each selection is a distinct, shareable URL.
  const selectCustomer = (id: string) => {
    setSelectedId(id);
    setTab('summary');
    setSearchParams(
      (prev) => {
        prev.set('id', id);
        return prev;
      },
      { replace: true },
    );
  };

  // Paginated customer list straight from the catalog API (server pagination over 75k+ customers).
  // isPlaceholderData is true while a new page is loading and the previous page is still shown — we use it to
  // swap the list for a spinner so paging gives clear loading feedback instead of stale rows.
  const { data, isPlaceholderData, isLoading } = useQuery<{
    customers: Customer[];
    total: number | null;
    hasNext: boolean;
    dowChart: DowChart;
  }>({
    queryKey: ['data-app', 'customers', 'page', page, PAGE_SIZE],
    queryFn: () => fetch(`/api/data-app/customers?page=${page}&pageSize=${PAGE_SIZE}`).then((r) => r.json()),
    placeholderData: (prev) => prev,
  });

  const customers = useMemo(() => data?.customers ?? [], [data]);
  const total = data?.total ?? null;
  const dow = data?.dowChart;
  const pageCount = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : data?.hasNext ? page + 1 : page;
  const safePage = page;
  // Search filters the current page only (the list is server-paginated across all customers).
  const paged = search ? customers.filter((x) => x.name.toLowerCase().includes(search.toLowerCase())) : customers;
  // Selection precedence: explicit click → ?id deep-link → first customer of page 1 (don't auto-jump when paging).
  const effectiveId = selectedId ?? urlId ?? (page === 1 ? (customers[0]?.id ?? null) : null);

  const found = customers.find((x) => x.id === effectiveId) ?? null;

  // describe (catalog identity) is only needed for a DEEP-LINKED customer that isn't on the current list page —
  // the list itself already carries name/city/country/segment/type. Keeps initial load free of detail calls.
  const { data: describe } = useQuery<{
    id: string;
    name: string;
    city: string;
    country: string;
    segment: string;
    type: string;
  }>({
    queryKey: ['data-app', 'customers', effectiveId, 'describe'],
    queryFn: () => fetch(`/api/data-app/customers/${encodeURIComponent(effectiveId ?? '')}`).then((r) => r.json()),
    enabled: !!effectiveId && !found,
    staleTime: 5 * 60 * 1000,
  });

  // Lightweight order-existence check, fetched on selection (incl. Summary) — gates every detail tab so we can
  // show "No Ordering Behavior Data" without loading the full order events.
  const { data: orderCount } = useQuery<{ hasOrders: boolean; total: number }>({
    queryKey: ['data-app', 'customer', effectiveId, 'order-count'],
    queryFn: () =>
      fetch(`/api/data-app/customer/${encodeURIComponent(effectiveId ?? '')}/order-count`).then((r) => r.json()),
    enabled: !!effectiveId,
    staleTime: 5 * 60 * 1000,
  });
  const ordersReady = orderCount !== undefined;
  const hasOrders = !!orderCount?.hasOrders;

  // The FULL order events (rows + line items + products) are deferred — only fetched once the user opens a detail
  // tab (Orders/Ordering-Behavior need the data; never on the Summary tab).
  const { data: custOrders } = useQuery<{
    past: PastOrder[];
    soEvents: Record<string, SoEvent>;
    products: { id: string; name: string }[];
    ordersLTM: number;
    lastOrderIn: string;
  }>({
    queryKey: ['data-app', 'customer', effectiveId, 'orders'],
    queryFn: () =>
      fetch(`/api/data-app/customer/${encodeURIComponent(effectiveId ?? '')}/orders`).then((r) => r.json()),
    enabled: !!effectiveId && tab !== 'summary' && hasOrders,
    staleTime: 5 * 60 * 1000,
  });
  const soEvents = custOrders?.soEvents ?? {};

  // Resolve the detail customer: the list entry (template + real identity) or a deep-link skeleton, overlaid with
  // the real orders fetched when a detail tab is opened.
  const base = found ?? (effectiveId && customers[0] ? { ...customers[0], id: effectiveId } : null);
  const c = base
    ? {
        ...base,
        name: describe?.name || base.name,
        city: describe?.city || base.city,
        country: describe?.country || base.country,
        ordersLTM: custOrders?.ordersLTM ?? base.ordersLTM,
        orders: { past: custOrders?.past ?? [], predicted: [] as PredictedOrder[] },
      }
    : null;

  // Detail tabs that depend on order events: show a loading state until the (deferred) orders call resolves, then
  // either the content or a single "No Ordering Behavior Data" message when the customer has no recorded orders.
  const orderGated = (node: React.ReactNode) =>
    !ordersReady ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 20px' }}>
        <Spinner size={26} />
      </div>
    ) : !hasOrders ? (
      <div style={{ padding: '48px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 30, opacity: 0.2, marginBottom: 8 }}>📭</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.t2, marginBottom: 4 }}>No Ordering Behavior Data</div>
        <div style={{ fontSize: 11.5, color: C.t3 }}>This customer has no recorded order events.</div>
      </div>
    ) : (
      node
    );

  return (
    <Page
      title="Customers"
      subheader="Behavioral intelligence for each customer — mined from your ERP transaction history. Click any customer to see what Keystone has learned."
      fillHeight
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          height: '100%',
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 8,
        }}
      >
        {/* LEFT: list */}
        <div
          style={{
            width: 220,
            flexShrink: 0,
            borderRight: `1px solid ${C.border}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: C.warm,
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
            <input
              type="text"
              placeholder="Search customers..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                goToPage(1);
              }}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 11.5,
                color: C.t1,
                background: C.warm,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {/* Pagination (top, under search) — server-paginated; ?page= permalink. nowrap + flexShrink:0 keep
                the buttons + "x / y · 10/page" label on one line even at high page numbers in the 220px panel. */}
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginTop: 8 }}
            >
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => goToPage(Math.max(1, safePage - 1))}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '3px 6px',
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: safePage <= 1 ? C.t3 : C.blue,
                  cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 10, color: C.t2, whiteSpace: 'nowrap' }}>
                {safePage} / {pageCount} <span style={{ color: C.t3 }}>· 10/page</span>
              </span>
              <button
                type="button"
                disabled={safePage >= pageCount}
                onClick={() => goToPage(Math.min(pageCount, safePage + 1))}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '3px 6px',
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: safePage >= pageCount ? C.t3 : C.blue,
                  cursor: safePage >= pageCount ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                Next ›
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {isPlaceholderData ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
                <Spinner size={26} />
              </div>
            ) : (
              paged.map((x) => (
                <div
                  key={x.id}
                  onClick={() => selectCustomer(x.id)}
                  style={{
                    padding: '9px 14px',
                    borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer',
                    background: effectiveId === x.id ? 'rgba(39,68,131,.08)' : '#fff',
                    borderLeft: effectiveId === x.id ? `3px solid ${C.blue}` : '3px solid transparent',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{x.name}</div>
                  </div>
                  <div style={{ fontSize: 9.5, color: C.t3, marginBottom: 3, overflowWrap: 'anywhere' }}>
                    {[x.city, x.country].filter(Boolean).join(', ') || '—'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        padding: '1px 6px',
                        borderRadius: 100,
                        background: riskBg(x.risk),
                        color: riskColor(x.risk),
                      }}
                    >
                      {x.riskLabel}
                    </span>
                    <span style={{ fontSize: 9, color: C.t3 }}>{x.revTrend} YTD</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: detail */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!c ? (
            isLoading || isPlaceholderData ? (
              // Deep-linked (?id=) and the list hasn't loaded yet — show a spinner, not the empty placeholder.
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size={28} />
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  color: C.t3,
                }}
              >
                <div style={{ fontSize: 32, opacity: 0.2 }}>👤</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.t2 }}>Select a customer</div>
                <div style={{ fontSize: 11, color: C.t3 }}>Click any customer to see their behavioral profile</div>
              </div>
            )
          ) : (
            <>
              {/* header */}
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: `1px solid ${C.border}`,
                  background: '#fff',
                  flexShrink: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.t3,
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                        marginBottom: 3,
                      }}
                    >
                      Customer · <span style={{ textTransform: 'none', fontFamily: 'monospace' }}>{c.id}</span>
                    </div>
                    <div
                      style={{ fontSize: 18, fontWeight: 700, color: C.t1, letterSpacing: '-0.02em', marginBottom: 2 }}
                    >
                      {c.name}
                    </div>
                    <div style={{ fontSize: 10.5, color: C.t3 }}>
                      {[c.city, c.country].filter(Boolean).join(', ')}
                      {c.city || c.country ? ' · ' : ''}Memory: {c.memoryLabel}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 100,
                      background: riskBg(c.risk),
                      color: riskColor(c.risk),
                      flexShrink: 0,
                    }}
                  >
                    {c.riskLabel}
                  </span>
                </div>
              </div>

              {/* tab bar */}
              <div
                style={{
                  display: 'flex',
                  borderBottom: `1px solid ${C.border}`,
                  background: '#fff',
                  flexShrink: 0,
                  padding: '0 20px',
                }}
              >
                {TABS.map((tdef) => (
                  <div
                    key={tdef.key}
                    onClick={() => setTab(tdef.key)}
                    style={{
                      padding: '10px 14px',
                      fontSize: 11.5,
                      fontWeight: tab === tdef.key ? 700 : 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      color: tab === tdef.key ? C.blue : C.t3,
                      borderBottom: tab === tdef.key ? `2px solid ${C.blue}` : '2px solid transparent',
                      marginBottom: -1,
                    }}
                  >
                    {tdef.label}
                  </div>
                ))}
              </div>

              {/* content */}
              <div
                style={{
                  flex: 1,
                  overflow: tab === 'orders' ? 'hidden' : 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  background: C.warm,
                }}
              >
                {tab === 'summary' && orderGated(<SummaryTab c={c} />)}
                {tab === 'orders' && orderGated(<OrdersTab c={c} soEvents={soEvents} />)}
                {tab === 'behavior' && orderGated(dow ? <BehaviorTab c={c} dow={dow} /> : null)}
                {tab === 'lifecycle' && orderGated(<LifecycleTab c={c} />)}
                {tab === 'patterns' && orderGated(<PatternsTab c={c} />)}
                {tab === 'economic' && orderGated(<EconomicTab c={c} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </Page>
  );
}
